import { useQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import {
  buildPaginatedEndpoint,
  convertDatesInObject,
  getNextPageParam,
} from '@/helpers/api'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { OpportunityType } from '@/types/Opportunity'
import type { Pursuit } from '@/types/pursuit/Pursuit'

export type UseLatestOpportunityPursuitProps = {
  orgId: string
  opportunity_type: OpportunityType
  opportunity_id: string
}

const fetchPursuits = async (
  orgId: string,
  opportunity_type: OpportunityType,
  opportunity_id: string,
  accessToken: string | undefined | null,
): Promise<Pursuit | null> => {
  if (accessToken === null || accessToken === undefined) {
    return null
  }
  const url = `${API_URL}/api/v1/organizations/${orgId}/pursuits?opportunity_type=${opportunity_type}&opportunity_id=${opportunity_id}`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  const data: PaginatedResponse<Pursuit> = await res.json()
  const latestPursuit = data.data.sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })
  if (latestPursuit.length === 0) {
    return null
  }
  return convertDatesInObject(latestPursuit[0])
}

export const useLatestOpportunityPursuit = ({
  orgId,
  opportunity_type,
  opportunity_id,
}: UseLatestOpportunityPursuitProps) => {
  const { accessToken, isLoggedIn, user } = useAuthInfo()
  return useQuery({
    queryFn: () =>
      fetchPursuits(orgId, opportunity_type, opportunity_id, accessToken),
    queryKey: ['pursuits', 'latest', orgId, opportunity_type, opportunity_id],
    staleTime: 0,
    // Default gcTime (not 0): the chat header remounts on every session
    // switch, and evicting this cache made the Create/View Pursuit button
    // null-flash on each remount. refetchOnMount: 'always' still revalidates;
    // the cached value just stays visible during the refetch.
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    notifyOnChangeProps: 'all', // Notify on any change to ensure re-renders
    enabled:
      !!isLoggedIn && !!accessToken && orgId !== '' && opportunity_id !== '',
  })
}
